if (!customElements.get('boreal-predictive-search')) {
  class BorealPredictiveSearch extends HTMLElement {
    connectedCallback() {
      if (this.events) return;
      this.input = this.querySelector('[data-boreal-search-input]');
      this.results = this.querySelector('[data-boreal-predictive-results]');
      this.toggle = this.querySelector('[data-boreal-search-toggle]');
      this.form = this.querySelector('form');
      this.header = this.closest('.bnp-desktop');
      this.endpoint = this.dataset.predictiveSearchUrl;
      this.cache = new Map();
      this.events = new AbortController();
      const options = { signal: this.events.signal };
      this.input.addEventListener('input', () => {
        this.close();
        this.timer = window.setTimeout(() => this.update(), 220);
      }, options);
      this.input.addEventListener('focus', () => this.update(), options);
      this.addEventListener('keydown', event => this.onKeydown(event), options);
      this.addEventListener('focusout', event => {
        if (event.relatedTarget && !this.contains(event.relatedTarget)) this.closeHeader();
      }, options);
      document.addEventListener('click', event => {
        if (!this.contains(event.target)) this.closeHeader();
      }, options);
      this.form.addEventListener('submit', event => {
        if (!this.input.value.trim()) event.preventDefault();
      }, options);
      if (this.toggle) {
        this.toggle.setAttribute('role', 'button');
        this.toggle.addEventListener('click', event => {
          event.preventDefault();
          if (this.form.hidden) {
            this.form.hidden = false;
            this.toggle.setAttribute('aria-expanded', 'true');
            this.header.classList.add('bnp-search-open');
            this.dispatchEvent(new CustomEvent('boreal:search-open', { bubbles: true }));
            this.input.focus();
          } else {
            this.closeHeader(true);
          }
        }, options);
        this.toggle.addEventListener('keydown', event => {
          if (event.key === ' ') {
            event.preventDefault();
            this.toggle.click();
          }
        }, options);
        window.matchMedia('(min-width: 1024px)').addEventListener('change', () => this.closeHeader(), options);
      }
    }

    disconnectedCallback() {
      this.closeHeader();
      this.events?.abort();
      this.events = null;
    }

    onKeydown(event) {
      if (event.key === 'Escape') {
        event.preventDefault();
        this.closeHeader(true);
        return;
      }
      if (event.target !== this.input || this.results.hidden) return;
      const choices = [...this.results.querySelectorAll('[role="option"]')];
      if (!choices.length) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        const direction = event.key === 'ArrowDown' ? 1 : -1;
        this.activeIndex = this.activeIndex < 0
          ? (direction === 1 ? 0 : choices.length - 1)
          : (this.activeIndex + direction + choices.length) % choices.length;
        choices.forEach((choice, index) => choice.setAttribute('aria-selected', String(index === this.activeIndex)));
        this.input.setAttribute('aria-activedescendant', choices[this.activeIndex].id);
        choices[this.activeIndex].scrollIntoView({ block: 'nearest' });
      } else if (event.key === 'Enter' && this.activeIndex >= 0) {
        event.preventDefault();
        choices[this.activeIndex].querySelector('a')?.click();
      }
    }

    async update() {
      this.request?.abort();
      this.request = null;
      const query = this.input.value.trim();
      if (query.length < 2 || (this.toggle && this.form.hidden)) {
        this.close();
        return;
      }
      if (this.cache.has(query)) {
        this.render(this.cache.get(query));
        return;
      }
      const request = new AbortController();
      this.request = request;
      this.setAttribute('loading', '');
      const parameters = new URLSearchParams({
        q: query,
        'resources[type]': 'product',
        'resources[limit]': '6',
        'resources[options][unavailable_products]': 'last',
        'resources[options][fields]': 'title,product_type,vendor,variants.title',
        section_id: 'boreal-predictive-search'
      });
      try {
        const response = await fetch(`${this.endpoint}?${parameters.toString()}`, { signal: request.signal });
        if (!response.ok) throw new Error(response.status);
        const markup = await response.text();
        if (this.request !== request || this.input.value.trim() !== query) return;
        const fragment = new DOMParser().parseFromString(markup, 'text/html');
        const section = fragment.querySelector('#shopify-section-boreal-predictive-search');
        const content = section ? section.innerHTML.trim() : '';
        this.cache.set(query, content);
        this.render(content);
      } catch (error) {
        if (error.name !== 'AbortError' && this.request === request) this.close();
      } finally {
        if (this.request === request) {
          this.request = null;
          this.removeAttribute('loading');
        }
      }
    }

    render(content) {
      if (!content) {
        this.close();
        return;
      }
      this.results.innerHTML = content;
      const listbox = this.results.querySelector('[role="listbox"]');
      if (listbox) {
        listbox.id = `${this.results.id}-listbox`;
        listbox.setAttribute('aria-label', this.results.querySelector('.boreal-search-suggestions__heading').textContent);
        this.input.setAttribute('aria-controls', listbox.id);
      }
      this.results.querySelectorAll('[role="option"]').forEach((option, index) => {
        option.id = `${this.results.id}-option-${index}`;
        option.setAttribute('aria-selected', 'false');
      });
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.results.hidden = false;
      this.input.setAttribute('aria-expanded', 'true');
    }

    close() {
      window.clearTimeout(this.timer);
      this.request?.abort();
      this.request = null;
      this.removeAttribute('loading');
      this.results.hidden = true;
      this.activeIndex = -1;
      this.input.removeAttribute('aria-activedescendant');
      this.input.setAttribute('aria-expanded', 'false');
      this.input.setAttribute('aria-controls', this.results.id);
    }

    closeHeader(restoreFocus = false) {
      this.close();
      if (!this.toggle) return;
      this.form.hidden = true;
      this.header.classList.remove('bnp-search-open');
      this.toggle.setAttribute('aria-expanded', 'false');
      if (restoreFocus) this.toggle.focus();
    }
  }
  customElements.define('boreal-predictive-search', BorealPredictiveSearch);
}
